import { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { gql, useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import axios from "axios";
import JSZip from "jszip";
import { useDarkModeCheck } from "../DashboardLayout";
import {
  GET_CUSTOMERS,
  GET_HOME_DATA,
  GET_SUSTAINABILITY_BANNER_DATA,
  GET_SUSTAINABILITY_ACHIEVEMENTS,
  GET_SC_DATA_LIST,
  GET_SC_DATA_LEADERBOARD,
  GET_SAFETY_CULTURE_HANDLEBAR,
  GET_AUDITS_DATA,
} from "../../../service/graphql/query";
import {
  previousMonthRangeSelect,
  inputDateFormat,
  sortTableFunction,
} from "../../../service/utils/functions";
import {
  CustomerDataEntry,
  SiteDataEntry,
} from "../../../service/utils/interfaces";
import {
  genericDocumentsTable,
  SortOrder,
} from "../../../service/utils/constants";
import { auditsDataFormat } from "../../../service/utils/parsers";
import { SortSettings } from "../../../component/sortable-table/SortableTable";
import * as Storage from "../../../service/storage";
import {
  customerListFormat,
  useCustomerFilters,
  useRequestGuard,
} from "../../../service/hooks/customerData";
import { 
  validateDownloadCompleteness, 
  createDownloadConfig, 
  DownloadProgress
} from '../../../utils/fileDownloadValidator';
import { trackDownload } from "../../../service/matomo";
import SustainabilityDashboardUI from "../../../ui/dashboard/sustainability/SustainabilityDashboardUI";
import {
  GET_TICKET_ACTIONS,
  UPSERT_TICKET_ACTION,
} from "../../../service/graphql/ticketActions";

type AuditNotesSection = {
  section: string;
  note: string;
};

type AuditNotesCategory = {
  category: string;
  sections: AuditNotesSection[];
};

type ProgressiveActionsCategory = {
  category: string;
  sections: {
    section: string;
    actions: {
      notesId: string;
      actionSorting: string;
      question: string;
      response: string;
      actionName: string;
      owner: string;
    }[];
  }[];
};

type ProgressiveActionItem = {
  category: string;
  section: string;
  notesId: string;
  actionSorting: string;
  question: string;
  response: string;
  actionName: string;
  owner: string;
  key: string;
};

type LeaderboardEntry = {
  auditId: string;
  occupier: string;
  dateCompleted: string;
  auditScorePercent: number;
  completed: number;
  total: number;
  percentDone: number;
};

type SustainabilityBannerPlacement =
  | "sustainabilityTeamOfferings"
  | "sustainabilityCalendar"
  | "volunteringEvents"
  | "relatedBlogpost";

type SustainabilityBannerItem = {
  id: string;
  placement: SustainabilityBannerPlacement;
  title: string;
  link: string;
  imageUrl: string;
  autoScroll: boolean;
  displayTitle: boolean;
  readMoreButton: boolean;
  createdAt: string;
};

type SustainabilityAchievementItem = {
  id: string;
  customerNumber: string;
  siteNumber: string;
  occupier: string;
  awardName: string;
  imageUrl: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
};


const SustainabilityDashboard = () => {
  const TICKET_ACTION_UPSERT_CHUNK_SIZE = 25;
  const apolloClient = useApolloClient();
  const { darkMode } = useDarkModeCheck();

  const getAuditOccupierFromPayload = useCallback((payload: any): string => {
    const raw = payload?.audit?.occupier;
    if (typeof raw === "string") return raw.trim();
    if (raw && typeof raw === "object") {
      const value = (raw as any)?.value;
      if (typeof value === "string") return value.trim();
      const label = (raw as any)?.label;
      if (typeof label === "string") return label.trim();
    }
    return "";
  }, []);

  const getTicketActionKey = useCallback(
    (
      category: string,
      section: string,
      question: string,
      response: string,
      notesId?: string,
      actionSorting?: string
    ): string => {
      return [category, section, question, response, notesId, actionSorting]
        .map((part) => String(part ?? "").trim())
        .join("||");
    },
    []
  );

  const getLegacyTicketActionKey = useCallback((category: string, actionName: string): string => {
    return `${String(category ?? "").trim()}||${String(actionName ?? "").trim()}`;
  }, []);

  const coerceToDate = useCallback((value: unknown): Date | null => {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    if (typeof value === "number" && Number.isFinite(value)) {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const asNumber = Number(trimmed);
      if (Number.isFinite(asNumber) && trimmed !== "") {
        const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }

      const d = new Date(trimmed);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    return null;
  }, []);

  const normalizeAuditDateCompletedForStrapi = useCallback(
    (value: unknown): string => {
      const d = coerceToDate(value);
      if (d) return d.toISOString();
      return value === null || value === undefined ? "" : String(value);
    },
    [coerceToDate]
  );

  const getCustomerName = useCallback((customer?: CustomerDataEntry): string => {
    const label = (customer as any)?.label;
    if (typeof label === "string" && label.trim() !== "") return label.trim();
    const customerField = (customer as any)?.customer;
    if (typeof customerField === "string" && customerField.trim() !== "") return customerField.trim();
    return "";
  }, []);

  const extractBracketValueAsInt = useCallback((value: unknown): number | null => {
    const text = String(value ?? "").trim();
    if (!text) return null;

    const match = text.match(/\(([^)]+)\)\s*$/);
    if (!match) return null;

    const bracketRaw = String(match[1] ?? "").trim();
    if (!bracketRaw) return null;

    // Site labels can include a leading '#' (for example '#1877').
    // Keep only digits so we always send GraphQL Int filters.
    const digitsOnly = bracketRaw.replace(/[^0-9]/g, "");
    if (!digitsOnly) return null;

    const parsed = Number.parseInt(digitsOnly, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() =>
    previousMonthRangeSelect()
  );
  const [startDate, endDate] = dateRange;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDataEntry>();
  const loadCustomerFilters = useCustomerFilters();
  const isNewLegacyAuditsRequest = useRequestGuard();
  const isNewScRequest = useRequestGuard();
  const [sitesPerCustomer, setSitesPerCustomer] = useState<SiteDataEntry[]>([]);
  // Customer whose sites are in sitesPerCustomer, so site-driven calls do not
  // go out with the previous customer's sites.
  const [sitesCustomerId, setSitesCustomerId] = useState<number>();

  // Sites are loaded for the selected customer only.
  useEffect(() => {
    if (!selectedCustomer) {
      setSitesPerCustomer([]);
      return;
    }
    let isCurrent = true;
    (async () => {
      const customer = await loadCustomerFilters(selectedCustomer);
      const filter = await Storage.FILTER_DATA.get();
      if (!isCurrent) return;
      // Sites, selected sites and the customer id change together, so no
      // site-driven call goes out with the previous customer's selection.
      const sites = customer.sites ?? [];
      setSitesPerCustomer(sites);
      setSelectedSites(sites.filter((_, idx) => filter.siteIndex?.includes(idx)));
      setSitesCustomerId(selectedCustomer.value);
    })();
    return () => {
      isCurrent = false;
    };
  }, [selectedCustomer, loadCustomerFilters]);
  const [selectedSites, setSelectedSites] = useState<SiteDataEntry[]>([]);
  const [reportData, setReportData] = useState<any>(null);
  const [auditOptions, setAuditOptions] = useState<
    {
      label: string;
      value: string;
      customerSite?: string;
      occupier?: string;
      dateCompleted?: any;
      handlebarUri?: string;
      carboneTemplateId?: string;
    }[]
  >([]);
  const [selectedAudit, setSelectedAudit] = useState<
    {
      label: string;
      value: string;
      customerSite?: string;
      occupier?: string;
      dateCompleted?: any;
      handlebarUri?: string;
      carboneTemplateId?: string;
    } | null
  >(null);
  const [width, setWidth] = useState<number>(0);
  const [username, setUsername] = useState<string>("User");
  const [email, setEmail] = useState<string>("user@example.com");
  const [exportInProgress, setExportInProgress] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);

  const [sustainabilityBanners, setSustainabilityBanners] = useState<SustainabilityBannerItem[]>([]);
  const [sustainabilityAchievements, setSustainabilityAchievements] = useState<SustainabilityAchievementItem[]>([]);

  const [progressiveActionGroups, setProgressiveActionGroups] = useState<ProgressiveActionsCategory[]>([]);
  const [ticketActionStatusByName, setTicketActionStatusByName] = useState<Record<string, boolean>>({});
  const [ticketActionIdByName, setTicketActionIdByName] = useState<Record<string, string>>({});
  const [ticketActionCompletedByByName, setTicketActionCompletedByByName] = useState<Record<string, string>>({});
  const [isTicketActionsLoading, setIsTicketActionsLoading] = useState<boolean>(false);

  const [leaderboardEntries, setLeaderboardEntries] = useState<LeaderboardEntry[]>([]);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState<boolean>(false);
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState<number>(0);
  const refreshLeaderboard = useCallback(() => setLeaderboardRefreshKey((k) => k + 1), []);

  const inFlightActionCreatesRef = useRef<Set<string>>(new Set());
  const [auditActionsTableHeader] = useState<string[]>([
    "Category",
    "Section",
    "Audit Responses",
    "Recommendations",
  ]);
  const [auditActionsTableRows, setAuditActionsTableRows] = useState<any[][]>([]);
  const [auditActionsSortSettings, setAuditActionsSortSettings] = useState<SortSettings | undefined>(undefined);
  const [auditActionsSearchParam, setAuditActionsSearchParam] = useState<string>("");

  const [isAuditNotesLoading, setIsAuditNotesLoading] = useState<boolean>(false);
  const [cachedSafetyCultureAuditId, setCachedSafetyCultureAuditId] = useState<string | null>(null);
  const [cachedSafetyCulturePayload, setCachedSafetyCulturePayload] = useState<any>(null);
  const [cachedAuditOccupier, setCachedAuditOccupier] = useState<string>("");

  const [legacyTableHeader, setLegacyTableHeader] = useState<any[]>(genericDocumentsTable);
  const [legacyTableRows, setLegacyTableRows] = useState<any[][]>([]);
  const [legacySortSettings, setLegacySortSettings] = useState<SortSettings | undefined>(undefined);
  const [legacySearchParam, setLegacySearchParam] = useState<string>("");
  const [legacyReordering, setLegacyReordering] = useState<boolean>(false);
  const [legacyAuditsByCustomerId, setLegacyAuditsByCustomerId] = useState<Record<string, any[]>>({});

  // GraphQL queries
  const [getCustomers, { data: customersData }] = useLazyQuery(GET_CUSTOMERS, {
    context: { apiName: "cms" },
    onCompleted: () => setIsLoading(false),
    onError: (err) => {
      console.log("Error fetching customers:", err);
      setIsLoading(false);
    },
  });

  const [getHomeData, { data: homeData }] = useLazyQuery(GET_HOME_DATA, {
    context: { apiName: "cms" },
    fetchPolicy: "network-only",
    onCompleted: () => {
      setIsLoading(false);
    },
    onError: (err) => {
      console.log("Error fetching home data:", err);
      setIsLoading(false);
    },
  });

  const [getSustainabilityBannerData] = useLazyQuery(GET_SUSTAINABILITY_BANNER_DATA, {
    context: { apiName: "cms" },
    fetchPolicy: "network-only",
    onError: (err) => {
      console.log("Error fetching Sustainability banner data:", err);
    },
  });

  const parseSustainabilityBannerRows = useCallback((rows: any[]): SustainabilityBannerItem[] => {
    return rows
      .map((row: any): SustainabilityBannerItem | null => {
        const id = row?.id ? String(row.id) : "";
        const attrs = row?.attributes;
        const placement = attrs?.placement as SustainabilityBannerPlacement | undefined;
        const title = typeof attrs?.title === "string" ? attrs.title : "";
        const link = typeof attrs?.link === "string" ? attrs.link : "";
        const autoScroll = Boolean(attrs?.autoScroll);
        const displayTitle = Boolean(attrs?.displayTitle);
        const readMoreButton = Boolean(attrs?.readMoreButton);
        const createdAt = typeof attrs?.createdAt === "string" ? attrs.createdAt : "";
        const imageUrl = attrs?.image?.data?.attributes?.url ? String(attrs.image.data.attributes.url) : "";

        if (!id || !placement) return null;
        if (!imageUrl) return null;

        return {
          id,
          placement,
          title,
          link,
          imageUrl,
          autoScroll,
          displayTitle,
          readMoreButton,
          createdAt,
        };
      })
      .filter(Boolean) as SustainabilityBannerItem[];
  }, []);

  const loadAllSustainabilityBanners = useCallback(async () => {
    try {
      const pageSize = 100;
      let page = 1;
      let pageCount = 1;
      const allRows: any[] = [];

      while (page <= pageCount) {
        const res = await getSustainabilityBannerData({
          context: { apiName: "cms" },
          variables: {
            pagination: {
              page,
              pageSize,
            },
          },
        });

        const data = res?.data?.sustainabilityBanners;
        const rows = Array.isArray(data?.data) ? data.data : [];
        allRows.push(...rows);

        const nextPageCount = Number(data?.meta?.pagination?.pageCount);
        pageCount = Number.isFinite(nextPageCount) && nextPageCount > 0 ? nextPageCount : 1;
        page += 1;
      }

      setSustainabilityBanners(parseSustainabilityBannerRows(allRows));
    } catch (err) {
      console.log("Error loading paginated Sustainability banners:", err);
      setSustainabilityBanners([]);
    }
  }, [getSustainabilityBannerData, parseSustainabilityBannerRows]);

  const [getSustainabilityAchievementsData, { loading: sustainabilityAchievementsLoading }] = useLazyQuery(
    GET_SUSTAINABILITY_ACHIEVEMENTS,
    {
      context: { apiName: "cms" },
      onCompleted: (data) => {
        const rows = data?.sustainabilityAchievements?.data ?? [];
        const parsed: SustainabilityAchievementItem[] = rows
          .map((row: any): SustainabilityAchievementItem | null => {
            const id = row?.id ? String(row.id) : "";
            const attrs = row?.attributes;
            const customerNumber =
              attrs?.customerNumber === null || attrs?.customerNumber === undefined
                ? ""
                : String(attrs.customerNumber).trim();
            const siteNumber =
              attrs?.siteNumber === null || attrs?.siteNumber === undefined
                ? ""
                : String(attrs.siteNumber).trim();
            const occupier = typeof attrs?.occupier === "string" ? attrs.occupier.trim() : "";
            const awardName = typeof attrs?.awardName === "string" ? attrs.awardName.trim() : "";
            const displayOrder = Number.isFinite(Number(attrs?.displayOrder))
              ? Number(attrs.displayOrder)
              : 0;
            const isActive = attrs?.isActive !== false;
            const createdAt = typeof attrs?.createdAt === "string" ? attrs.createdAt : "";
            const imageUrl = attrs?.image?.data?.attributes?.url
              ? String(attrs.image.data.attributes.url)
              : "";

            if (!id) return null;

            return {
              id,
              customerNumber,
              siteNumber,
              occupier,
              awardName,
              imageUrl,
              displayOrder,
              isActive,
              createdAt,
            };
          })
          .filter(Boolean) as SustainabilityAchievementItem[];

        setSustainabilityAchievements(parsed);
      },
      onError: (err) => {
        console.log("Error fetching Sustainability achievements data:", err);
        setSustainabilityAchievements([]);
      },
    }
  );

  const [getScDataList, { data: scDataListData, loading: scDataListLoading }] =
    useLazyQuery(GET_SC_DATA_LIST, {
      context: { apiName: "middleware" },
      fetchPolicy: "network-only",
      onError: (err) => {
        console.log("Error fetching SafetyCulture audit list:", err);
      },
    });

  const [getScDataLeaderboard, { data: scLeaderboardData, loading: scLeaderboardLoading }] =
    useLazyQuery(GET_SC_DATA_LEADERBOARD, {
      context: { apiName: "middleware" },
      fetchPolicy: "network-only",
      onError: (err) => {
        console.log("Error fetching SafetyCulture leaderboard data:", err);
      },
    });

  const [getSafetyCultureHandlebar] = useLazyQuery(
    GET_SAFETY_CULTURE_HANDLEBAR,
    {
      context: { apiName: "middleware" },
      fetchPolicy: "network-only",
      onError: (err) => {
        console.log("Error fetching SafetyCulture payload:", err);
      },
    }
  );

  const [getTicketActions] = useLazyQuery(GET_TICKET_ACTIONS, {
    context: { apiName: "cms" },
    fetchPolicy: "network-only",
    onError: (err) => {
      console.log("Error fetching ticket actions:", err);
    },
  });

  const [upsertTicketAction] = useMutation(UPSERT_TICKET_ACTION, {
    context: { apiName: "cms" },
    onError: (err) => {
      console.log("Error upserting ticket action:", err);
    },
    errorPolicy: "all",
  });

  const upsertTicketActionsBulk = useCallback(
    async (inputs: Record<string, any>[]) => {
      if (!Array.isArray(inputs) || inputs.length === 0) return [] as Array<{ id: string | null }>;

      const allResults: Array<{ id: string | null }> = [];

      for (let offset = 0; offset < inputs.length; offset += TICKET_ACTION_UPSERT_CHUNK_SIZE) {
        const chunk = inputs.slice(offset, offset + TICKET_ACTION_UPSERT_CHUNK_SIZE);

        const variableDefinitions = chunk
          .map((_, idx) => `$input${idx}: TicketActionUpsertInput!`)
          .join(", ");

        const mutationFields = chunk
          .map(
            (_, idx) =>
              `m${idx}: upsertTicketAction(data: $input${idx}) { data { id } }`
          )
          .join("\n");

        const mutation = gql`
          mutation BulkUpsertTicketActions(${variableDefinitions}) {
            ${mutationFields}
          }
        `;

        const variables = chunk.reduce((acc, input, idx) => {
          acc[`input${idx}`] = input;
          return acc;
        }, {} as Record<string, any>);

        const result = await apolloClient.mutate({
          mutation,
          variables,
          context: { apiName: "cms" },
        });

        const chunkResults = chunk.map((_, idx) => ({
          id: result?.data?.[`m${idx}`]?.data?.id ? String(result.data[`m${idx}`].data.id) : null,
        }));

        allResults.push(...chunkResults);
      }

      return allResults;
    },
    [apolloClient, TICKET_ACTION_UPSERT_CHUNK_SIZE]
  );

  const extractProgressiveActions = useCallback((payload: any): ProgressiveActionsCategory[] => {
    const raw = Array.isArray(payload?.actions)
      ? payload.actions
      : Array.isArray(payload?.appendix)
      ? payload.appendix
      : null;
    if (!Array.isArray(raw)) return [];

    // Prefer using the same section names as Audit Outcomes when actions don't explicitly carry one.
    // This is derived from the same payload shapes handled by `extractAuditActionsTableRows`.
    const fallbackSectionsByCategory = new Map<string, string[]>();

    const pushFallbackSection = (category: unknown, section: unknown) => {
      const cat = String(category ?? "").trim();
      const sec = String(section ?? "").trim();
      if (!cat || !sec) return;
      const existing = fallbackSectionsByCategory.get(cat) ?? [];
      if (!existing.some((s) => s.toLowerCase() === sec.toLowerCase())) {
        fallbackSectionsByCategory.set(cat, [...existing, sec]);
      }
    };

    const notesRoot = Array.isArray(payload?.notesAndImages)
      ? payload.notesAndImages
      : Array.isArray(payload?.notes)
      ? payload.notes
      : [];

    notesRoot.forEach((entry: any) => {
      const category = entry?.category;
      const sections = Array.isArray(entry?.sections) ? entry.sections : [];
      sections.forEach((sec: any) => pushFallbackSection(category, sec?.section));
    });

    const recRoot = Array.isArray(payload?.recommendations) ? payload.recommendations : [];
    recRoot.forEach((entry: any) => {
      const category = entry?.category;
      const sections = Array.isArray(entry?.sections) ? entry.sections : [];
      sections.forEach((sec: any) => pushFallbackSection(category, sec?.section));
    });

    const grouped = new Map<
      string,
      Map<
        string,
        {
          notesId: string;
          actionSorting: string;
          question: string;
          response: string;
          actionName: string;
          owner: string;
        }[]
      >
    >();

    const readSection = (value: any): string => {
      const candidates = [
        value?.section,
        value?.Section,
        value?.sectionName,
        value?.section_name,
        value?.auditSection,
        value?.audit_section,
      ];
      for (const v of candidates) {
        if (typeof v === "string") {
          const normalized = v.replace(/\r\n?/g, "\n").trim();
          if (normalized !== "") return normalized;
        }
      }
      return "";
    };

    const readActionText = (value: any): string => {
      const actionsArray = Array.isArray(value?.actions) ? value.actions : null;
      if (actionsArray) {
        const merged = actionsArray
          .map((v: any) => (typeof v === "string" ? v.trim() : ""))
          .filter(Boolean)
          .join("\n");
        if (merged) return merged;
      }

      const candidates = [
        value?.action,
        value?.Action,
      ];
      for (const v of candidates) {
        if (typeof v === "string" && v.trim() !== "") return v.trim();
      }
      return "";
    };

    const readOwner = (value: any): string => {
      const candidates = [value?.actionowner, value?.actionOwner, value?.owner, value?.Owner];
      for (const v of candidates) {
        if (v === null || v === undefined) continue;
        const normalized = String(v).trim();
        if (!normalized || normalized === "0") continue;
        return normalized;
      }
      return "";
    };

    const readNotesId = (value: any): string => {
      const candidates = [value?.notesId, value?.notes_id, value?.noteId, value?.note_id];
      for (const v of candidates) {
        if (v === null || v === undefined) continue;
        const normalized = String(v).trim();
        if (normalized !== "") return normalized;
      }
      return "";
    };

    const readActionSorting = (value: any): string => {
      const candidates = [
        value?.actionSorting,
        value?.action_sorting,
        value?.sorting,
        value?.sortOrder,
        value?.order,
      ];
      for (const v of candidates) {
        if (v === null || v === undefined) continue;
        const normalized = String(v).trim();
        if (normalized !== "") return normalized;
      }
      return "";
    };

    const readQuestion = (value: any): string => {
      const candidates = [
        value?.question,
        value?.Question,
        value?.auditQuestion,
        value?.audit_question,
        value?.criteria,
      ];
      for (const v of candidates) {
        if (typeof v === "string" && v.trim() !== "") return v.trim();
      }
      return "";
    };

    const readResponse = (value: any): string => {
      const candidates = [
        value?.response,
        value?.Response,
        value?.auditResponse,
        value?.audit_response,
        value?.answer,
      ];
      for (const v of candidates) {
        if (typeof v === "string" && v.trim() !== "") return v.trim();
      }
      return "";
    };

    const upsertAction = (
      category: string,
      sectionRaw: string,
      questionRaw: string,
      responseRaw: string,
      notesIdRaw: string,
      actionSortingRaw: string,
      actionText: string,
      ownerRaw: string
    ) => {
      if (!actionText) return;

      // Keep each source row as one action even if it contains newlines.
      const normalizedActionText = String(actionText).replace(/\r\n?/g, "\n").trim();
      if (!normalizedActionText) return;

      const actionName = normalizedActionText;
      const owner = String(ownerRaw ?? "").trim();
      if (!actionName) return;

      const fallbackSections = fallbackSectionsByCategory.get(category) ?? [];
      const section =
        sectionRaw ||
        (fallbackSections.length === 1 ? fallbackSections[0] : fallbackSections[0] ?? "") ||
        "General";
      const notesId = String(notesIdRaw ?? "").trim();
      const actionSorting = String(actionSortingRaw ?? "").trim();
      const question = String(questionRaw ?? "").trim();
      const response = String(responseRaw ?? "").trim();

      if (!grouped.has(category)) grouped.set(category, new Map());
      const bySection = grouped.get(category)!;
      const existing = bySection.get(section) ?? [];
      bySection.set(section, [
        ...existing,
        {
          notesId,
          actionSorting,
          question,
          response,
          actionName,
          owner,
        },
      ]);
    };

    raw.forEach((entry: any) => {
      const category = typeof entry?.category === "string" ? entry.category.trim() : "";
      if (!category) return;

      const sectionRaw = readSection(entry);
      const questionRaw = readQuestion(entry);
      const responseRaw = readResponse(entry);
      const notesIdRaw = readNotesId(entry);
      const actionSortingRaw = readActionSorting(entry);
      const actionText = readActionText(entry);
      const ownerRaw = readOwner(entry);
      upsertAction(
        category,
        sectionRaw,
        questionRaw,
        responseRaw,
        notesIdRaw,
        actionSortingRaw,
        actionText,
        ownerRaw
      );
    });

    return Array.from(grouped.entries()).map(([category, bySection]) => ({
      category,
      sections: Array.from(bySection.entries()).map(([section, actions]) => ({
        section,
        actions,
      })),
    }));
  }, []);

  const extractAuditActionsTableRows = useCallback((payload: any): any[][] => {
    const rows = new Map<string, { category: string; section: string; notes: string; recs: string }>();

    const normalizeMultilineCell = (value: unknown): string => {
      if (value === null || value === undefined) return "";
      let text = String(value);

      // Normalize line endings
      text = text.replace(/\r\n?/g, "\n");

      // Remove leading/trailing whitespace and prevent blank lines at the start
      text = text.trim();
      text = text.replace(/^\n+/, "");

      // Collapse excessive blank lines (including those with spaces/tabs) to max 2 newlines
      text = text.replace(/\n[\t ]*\n([\t ]*\n)+/g, "\n\n");

      // Ensure no trailing blank lines
      text = text.replace(/\n+$/, "");

      return text;
    };

    const upsert = (category: string, section: string, patch: Partial<{ notes: string; recs: string }>) => {
      const cat = category.trim();
      const sec = section.trim();
      if (!cat && !sec) return;
      const key = `${cat}||${sec}`;
      const existing = rows.get(key) ?? { category: cat, section: sec, notes: "", recs: "" };
      rows.set(key, {
        ...existing,
        notes: patch.notes !== undefined && patch.notes !== null ? String(patch.notes) : existing.notes,
        recs: patch.recs !== undefined && patch.recs !== null ? String(patch.recs) : existing.recs,
      });
    };

    const notesRoot = Array.isArray(payload?.notesAndImages)
      ? payload.notesAndImages
      : Array.isArray(payload?.notes)
      ? payload.notes
      : [];

    notesRoot.forEach((entry: any) => {
      const category = typeof entry?.category === "string" ? entry.category : "";
      const sections = Array.isArray(entry?.sections) ? entry.sections : [];
      sections.forEach((sec: any) => {
        const section = typeof sec?.section === "string" ? sec.section : "";
        const noteValue =
          typeof sec?.note === "string"
            ? sec.note
            : typeof sec?.notes === "string"
            ? sec.notes
            : typeof sec?.notes?.notes === "string"
            ? sec.notes.notes
            : "";

        const normalized = normalizeMultilineCell(noteValue);
        if (normalized) upsert(category, section, { notes: normalized });
      });
    });

    const recRoot = Array.isArray(payload?.recommendations) ? payload.recommendations : [];
    recRoot.forEach((entry: any) => {
      const category = typeof entry?.category === "string" ? entry.category : "";

      // Supports both of these shapes:
      // 1) { category, sections: [{ section, recommendation }] }
      // 2) { category, recommendation }  (category-level recommendation)
      const entryLevelRec =
        typeof entry?.recommendation === "string"
          ? entry.recommendation
          : typeof entry?.recommendations === "string"
          ? entry.recommendations
          : typeof entry?.recommendations?.recommendation === "string"
          ? entry.recommendations.recommendation
          : "";

      const sections = Array.isArray(entry?.sections) ? entry.sections : [];

      if (sections.length > 0) {
        sections.forEach((sec: any) => {
          const section = typeof sec?.section === "string" ? sec.section : "";
          const recValue =
            typeof sec?.recommendation === "string"
              ? sec.recommendation
              : typeof sec?.recommendations === "string"
              ? sec.recommendations
              : typeof sec?.recommendations?.recommendation === "string"
              ? sec.recommendations.recommendation
              : "";

          const normalized = normalizeMultilineCell(recValue);
          if (normalized) upsert(category, section, { recs: normalized });
        });
        return;
      }

      // If we only have a category-level recommendation, apply it to existing section rows
      // for that category if present; otherwise create a category-only row.
      const normalizedEntryLevelRec = normalizeMultilineCell(entryLevelRec);
      if (normalizedEntryLevelRec) {
        const cat = category.trim();
        const existingSectionsForCategory = Array.from(rows.values())
          .filter((r) => r.category === cat)
          .map((r) => r.section);

        if (existingSectionsForCategory.length > 0) {
          existingSectionsForCategory.forEach((sec) =>
            upsert(cat, sec, { recs: normalizedEntryLevelRec })
          );
        } else {
          upsert(cat, "", { recs: normalizedEntryLevelRec });
        }
      }
    });

    return Array.from(rows.values()).map((r) => [r.category, r.section, r.notes, r.recs]);
  }, []);

  const resetAuditActionsTable = useCallback(() => {
    setAuditActionsSearchParam("");
    setAuditActionsSortSettings(undefined);
  }, []);

  const resetTicketActions = useCallback(() => {
    setTicketActionStatusByName({});
    setTicketActionIdByName({});
    setTicketActionCompletedByByName({});
  }, []);

  // Prefetch and parse the payload used for Carbone export as soon as the audit is selected.
  // This allows us to display notesAndImages in the "Audit Outcomes" widget.
  useEffect(() => {
    let isCurrent = true;

    (async () => {
      if (!selectedAudit?.value) {
        setProgressiveActionGroups([]);
        setAuditActionsTableRows([]);
        setIsAuditNotesLoading(false);
        resetTicketActions();
        setCachedAuditOccupier("");
        return;
      }

      if (!selectedCustomer?.value) {
        setProgressiveActionGroups([]);
        setAuditActionsTableRows([]);
        setIsAuditNotesLoading(false);
        resetTicketActions();
        setCachedAuditOccupier("");
        return;
      }

      try {
        setIsAuditNotesLoading(true);

        const handlebarResult = await getSafetyCultureHandlebar({
          variables: {
            auditId: selectedAudit.value,
            templateUri:
              (typeof selectedAudit?.handlebarUri === "string" ? selectedAudit.handlebarUri.trim() : "") ||
              process.env.REACT_APP_SC_HANDLEBAR_TEMPLATE_URL,
            customerId: parseInt(String(selectedCustomer.value), 10),
          },
        });

        let payload = handlebarResult.data?.getSafetyCultureHandlebar;
        if (!payload) {
          if (!isCurrent) return;
          setProgressiveActionGroups([]);
          setAuditActionsTableRows([]);
          return;
        }

        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch {
            // If payload isn't valid JSON, we can't extract notesAndImages.
          }
        }

        if (!isCurrent) return;

        setCachedSafetyCultureAuditId(String(selectedAudit.value));
        setCachedSafetyCulturePayload(payload);
        setCachedAuditOccupier(getAuditOccupierFromPayload(payload));
        resetAuditActionsTable();
        setProgressiveActionGroups(extractProgressiveActions(payload));
        setAuditActionsTableRows(extractAuditActionsTableRows(payload));
      } catch (err) {
        if (!isCurrent) return;
        console.log("Error prefetching SafetyCulture payload for notes:", err);
        setProgressiveActionGroups([]);
        setAuditActionsTableRows([]);
        setCachedAuditOccupier("");
      } finally {
        if (!isCurrent) return;
        setIsAuditNotesLoading(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [
    selectedAudit,
    selectedCustomer,
    getSafetyCultureHandlebar,
    extractProgressiveActions,
    extractAuditActionsTableRows,
    resetAuditActionsTable,
    resetTicketActions,
    getAuditOccupierFromPayload,
  ]);

  // Load previously saved ticket action statuses from Strapi CMS.
  // NOTE: These are shared across users for the same customer + audit.
  useEffect(() => {
    let isCurrent = true;

    (async () => {
      const customerName = getCustomerName(selectedCustomer);
      if (!selectedAudit?.value || !customerName) {
        resetTicketActions();
        return;
      }

      try {
        setIsTicketActionsLoading(true);

        const res = await getTicketActions({
          context: { apiName: "cms" },
          variables: {
            // Audit ids are unique across customers, so rows are matched on the
            // audit only and shared by everyone who sees it.
            filters: {
              auditId: { eq: String(selectedAudit.value) },
            },
          },
        });

        const items = res?.data?.ticketActions?.data ?? [];

        const nextStatus: Record<string, boolean> = {};
        const nextIds: Record<string, string> = {};
        const nextCompletedBy: Record<string, string> = {};

        // If duplicates exist (e.g., historic per-user rows), pick the most recently updated.
        const bestRowByActionKey = new Map<
          string,
          { id: string; status: boolean; username: string; updatedAt: string }
        >();

        items.forEach((row: any) => {
          const id = row?.id ? String(row.id) : "";
          const attrs = row?.attributes;
          const category = typeof attrs?.category === "string" ? attrs.category.trim() : "";
          const section = typeof attrs?.section === "string" ? attrs.section.trim() : "";
          const notesId = attrs?.notesId === null || attrs?.notesId === undefined ? "" : String(attrs.notesId).trim();
          const actionSorting =
            attrs?.actionSorting === null || attrs?.actionSorting === undefined
              ? ""
              : String(attrs.actionSorting).trim();
          const question = typeof attrs?.question === "string" ? attrs.question.trim() : "";
          const response = typeof attrs?.response === "string" ? attrs.response.trim() : "";
          const actionName = typeof attrs?.actionName === "string" ? attrs.actionName : "";
          const status = Boolean(attrs?.status);
          const rowUsername = typeof attrs?.username === "string" ? attrs.username : "";
          const updatedAt = typeof attrs?.updatedAt === "string" ? attrs.updatedAt : "";
          if (!id) return;

          const key = getTicketActionKey(
            category,
            section,
            question,
            response,
            notesId,
            actionSorting
          );
          const legacyKey = getLegacyTicketActionKey(category, actionName);

          const existing = bestRowByActionKey.get(key);
          if (!existing) {
            bestRowByActionKey.set(key, {
              id,
              status,
              username: rowUsername,
              updatedAt,
            });
            if (legacyKey && !bestRowByActionKey.has(legacyKey)) {
              bestRowByActionKey.set(legacyKey, {
                id,
                status,
                username: rowUsername,
                updatedAt,
              });
            }
            return;
          }

          const existingTime = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
          const nextTime = updatedAt ? new Date(updatedAt).getTime() : 0;
          if (nextTime >= existingTime) {
            bestRowByActionKey.set(key, {
              id,
              status,
              username: rowUsername,
              updatedAt,
            });
            if (legacyKey) {
              bestRowByActionKey.set(legacyKey, {
                id,
                status,
                username: rowUsername,
                updatedAt,
              });
            }
          }
        });

        bestRowByActionKey.forEach((best, key) => {
          nextIds[key] = best.id;
          nextStatus[key] = best.status;
          nextCompletedBy[key] = best.username;
        });

        if (!isCurrent) return;
        setTicketActionStatusByName(nextStatus);
        setTicketActionIdByName(nextIds);
        setTicketActionCompletedByByName(nextCompletedBy);
      } catch (err) {
        if (!isCurrent) return;
        console.log("Error loading ticket actions:", err);
        resetTicketActions();
      } finally {
        if (!isCurrent) return;
        setIsTicketActionsLoading(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [
    selectedAudit,
    selectedCustomer,
    getTicketActions,
    resetTicketActions,
    getCustomerName,
    getTicketActionKey,
    getLegacyTicketActionKey,
  ]);

  const allProgressiveActions = useMemo<ProgressiveActionItem[]>(() => {
    const items: ProgressiveActionItem[] = [];

    progressiveActionGroups.forEach((group) => {
      const category = String(group?.category ?? "").trim();
      const sections = Array.isArray((group as any)?.sections) ? (group as any).sections : [];
      sections.forEach((sec: any) => {
        const section = String(sec?.section ?? "").trim() || "General";
        const actions = Array.isArray(sec?.actions) ? sec.actions : [];
        actions.forEach((actionItem: any) => {
          const actionName = String(actionItem?.actionName ?? "").trim();
          const question = String(actionItem?.question ?? "").trim();
          const response = String(actionItem?.response ?? "").trim();
          const notesId = String(actionItem?.notesId ?? "").trim();
          const actionSorting = String(actionItem?.actionSorting ?? "").trim();
          const owner = String(actionItem?.owner ?? "").trim();
          if (!actionName) return;
          const key = getTicketActionKey(
            category,
            section,
            question,
            response,
            notesId,
            actionSorting
          );
          items.push({
            category,
            section,
            notesId,
            actionSorting,
            question,
            response,
            actionName,
            owner,
            key,
          });
        });
      });
    });

    return items;
  }, [progressiveActionGroups, getTicketActionKey]);

  // Ensure every progressive action is persisted in Strapi as soon as the audit actions are loaded.
  // This guarantees we always have the full task list per audit, not just the completed ones.
  useEffect(() => {
    let isCurrent = true;

    (async () => {
      const customerName = getCustomerName(selectedCustomer);
      const auditId = selectedAudit?.value ? String(selectedAudit.value) : "";
      if (!customerName || !auditId) return;
      if (isTicketActionsLoading) return;
      if (!allProgressiveActions || allProgressiveActions.length === 0) return;
      // Right after a customer or audit switch the previous selection is still
      // loaded, and saving then copied one audit's actions under another
      // customer or audit. Only save once audit, actions and customer match.
      if (cachedSafetyCultureAuditId !== auditId) return;
      if (sitesCustomerId !== selectedCustomer?.value) return;

      // Source occupier primarily from audit JSON payload (audit.occupier)
      const auditOccupier = (cachedAuditOccupier || String(selectedAudit?.customerSite ?? "")).trim();
      const auditDateCompleted = normalizeAuditDateCompletedForStrapi(selectedAudit?.dateCompleted);
      const auditCustomerSite = String(selectedAudit?.customerSite ?? "").trim();

      const createdIds: Record<string, string> = {};
      const queuedOperations: Array<{
        key: string;
        createKey: string | null;
        isCreate: boolean;
        input: Record<string, any>;
      }> = [];

      for (const item of allProgressiveActions) {
        if (!isCurrent) return;
        const { category, section, notesId, actionSorting, question, response, actionName, owner, key } = item;
        if (!actionName) continue;

        const existingId =
          ticketActionIdByName[key] ??
          ticketActionIdByName[getLegacyTicketActionKey(category, actionName)];
        if (existingId) {
          // Backfill compulsory audit metadata on pre-existing rows.
          const existingStatus =
            ticketActionStatusByName[key] ??
            ticketActionStatusByName[getLegacyTicketActionKey(category, actionName)] ??
            false;
          queuedOperations.push({
            key,
            createKey: null,
            isCreate: false,
            input: {
              customer: customerName,
              auditId,
              auditLabel: String(selectedAudit?.label ?? ""),
              auditOccupier,
              auditDateCompleted,
              ...(auditCustomerSite ? { customerSite: auditCustomerSite } : {}),
              category,
              section,
              notesId,
              actionSorting,
              question,
              response,
              action: actionName,
              owner,
              status: existingStatus,
            },
          });
          continue;
        }

        const createKey = `${customerName}||${auditId}||${key}`;
        if (inFlightActionCreatesRef.current.has(createKey)) continue;
        inFlightActionCreatesRef.current.add(createKey);

        queuedOperations.push({
          key,
          createKey,
          isCreate: true,
          input: {
            customer: customerName,
            ...(auditCustomerSite ? { customerSite: auditCustomerSite } : {}),
            auditId,
            auditLabel: String(selectedAudit?.label ?? ""),
            auditOccupier,
            auditDateCompleted,
            category,
            section,
            notesId,
            actionSorting,
            question,
            response,
            action: actionName,
            owner,
            status: false,
          },
        });
      }

      try {
        const results = await upsertTicketActionsBulk(
          queuedOperations.map((operation) => operation.input)
        );

        results.forEach((result, idx) => {
          const operation = queuedOperations[idx];
          if (!operation?.isCreate) return;
          if (!result?.id) return;
          createdIds[operation.key] = String(result.id);
        });
      } catch (err) {
        console.log("Error bulk upserting ticket actions. Falling back to sequential upserts:", err);
        for (const operation of queuedOperations) {
          if (!isCurrent) return;
          try {
            const upserted = await upsertTicketAction({
              context: { apiName: "cms" },
              variables: { input: operation.input },
            });

            if (!operation.isCreate) continue;

            const newId = upserted?.data?.upsertTicketAction?.data?.id;
            if (newId) createdIds[operation.key] = String(newId);
          } catch (fallbackErr) {
            console.log("Error upserting ticket action in fallback mode:", fallbackErr);
          }
        }
      } finally {
        queuedOperations.forEach((operation) => {
          if (operation.createKey) {
            inFlightActionCreatesRef.current.delete(operation.createKey);
          }
        });
      }

      if (!isCurrent) return;
      const keys = Object.keys(createdIds);
      if (keys.length > 0) {
        setTicketActionIdByName((current) => ({
          ...current,
          ...createdIds,
        }));
        refreshLeaderboard();
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [
    allProgressiveActions,
    selectedAudit,
    selectedCustomer,
    cachedSafetyCultureAuditId,
    sitesCustomerId,
    isTicketActionsLoading,
    ticketActionIdByName,
    getCustomerName,
    upsertTicketAction,
    upsertTicketActionsBulk,
    normalizeAuditDateCompletedForStrapi,
    refreshLeaderboard,
    cachedAuditOccupier,
    getLegacyTicketActionKey,
    ticketActionStatusByName,
  ]);

  const handleToggleTicketAction = useCallback(
    async (
      category: string,
      section: string,
      question: string,
      response: string,
      notesId: string,
      actionSorting: string,
      actionName: string,
      owner: string,
      checked: boolean
    ) => {
      console.log("[TicketActions] Toggle", {
        category,
        section,
        question,
        response,
        notesId,
        actionSorting,
        actionName,
        checked,
      });
      const customerName = getCustomerName(selectedCustomer);
      if (!selectedAudit?.value || !customerName) {
        console.warn("[TicketActions] Missing selected audit/customer; not saving.", {
          auditId: selectedAudit?.value,
          customer: customerName,
        });
        return;
      }
      if (!actionName) return;

      const actionKey = getTicketActionKey(
        category,
        section,
        question,
        response,
        notesId,
        actionSorting
      );
      const legacyActionKey = getLegacyTicketActionKey(category, actionName);

      const previous =
        ticketActionStatusByName[actionKey] ?? ticketActionStatusByName[legacyActionKey] ?? false;
      const previousCompletedBy =
        ticketActionCompletedByByName[actionKey] ?? ticketActionCompletedByByName[legacyActionKey] ?? "";

      // Optimistic UI update
      setTicketActionStatusByName((current) => ({
        ...current,
        [actionKey]: checked,
      }));

      setTicketActionCompletedByByName((current) => ({
        ...current,
        [actionKey]: checked ? String(username ?? "") : "",
      }));

      try {
        const existingId = ticketActionIdByName[actionKey] ?? ticketActionIdByName[legacyActionKey];

        // Source occupier primarily from audit JSON payload (audit.occupier)
        const auditOccupier = (cachedAuditOccupier || String(selectedAudit?.customerSite ?? "")).trim();
        const auditDateCompleted = normalizeAuditDateCompletedForStrapi(selectedAudit?.dateCompleted);
        const auditCustomerSite = String(selectedAudit?.customerSite ?? "").trim();

        const shouldSetUser = checked && Boolean(username) && Boolean(email);
        const userPatch = checked
          ? shouldSetUser
            ? { username, email }
            : {}
          : { username: "", email: "" };

        if (existingId) {
          await upsertTicketAction({
            context: { apiName: "cms" },
            variables: {
              input: {
                customer: customerName,
                auditId: String(selectedAudit.value),
                status: checked,
                auditLabel: String(selectedAudit.label ?? ""),
                auditOccupier,
                auditDateCompleted,
                ...(auditCustomerSite ? { customerSite: auditCustomerSite } : {}),
                category,
                section,
                notesId,
                actionSorting,
                question,
                response,
                action: actionName,
                owner,
                ...userPatch,
              },
            },
          });
        } else {
          if (!checked) return;
          const upserted = await upsertTicketAction({
            context: { apiName: "cms" },
            variables: {
              input: {
                customer: customerName,
                ...(auditCustomerSite ? { customerSite: auditCustomerSite } : {}),
                auditId: String(selectedAudit.value),
                auditLabel: String(selectedAudit.label ?? ""),
                auditOccupier,
                auditDateCompleted,
                category,
                section,
                notesId,
                actionSorting,
                question,
                response,
                action: actionName,
                owner,
                status: checked,
                ...(shouldSetUser ? { username, email } : {}),
              },
            },
          });

          const newId = upserted?.data?.upsertTicketAction?.data?.id;
          if (newId) {
            setTicketActionIdByName((current) => ({
              ...current,
              [actionKey]: String(newId),
            }));
          }
        }

        refreshLeaderboard();
      } catch (err) {
        console.log("Error saving ticket action:", err);
        // Revert on failure
        setTicketActionStatusByName((current) => ({
          ...current,
          [actionKey]: previous,
        }));
        setTicketActionCompletedByByName((current) => ({
          ...current,
          [actionKey]: previous ? previousCompletedBy : "",
        }));
      }
    },
    [
      selectedAudit,
      selectedCustomer,
      getCustomerName,
      username,
      email,
      ticketActionStatusByName,
      ticketActionIdByName,
      ticketActionCompletedByByName,
      upsertTicketAction,
      normalizeAuditDateCompletedForStrapi,
      refreshLeaderboard,
      cachedAuditOccupier,
      getTicketActionKey,
      getLegacyTicketActionKey,
    ]
  );

  // Leaderboard:
  // - Audit rows come from SafetyCulture (GET_SC_DATA_LEADERBOARD)
  // - Audit Score comes from PointPercentage * 100
  // - Count comes from ActionCount
  // - Actions Completed comes from Strapi ticket-actions (status=true) for matching auditId
  useEffect(() => {
    let isCurrent = true;

    (async () => {
      const customerName = getCustomerName(selectedCustomer);
      if (!customerName) {
        setLeaderboardEntries([]);
        setIsLeaderboardLoading(false);
        return;
      }

      if (scLeaderboardLoading) {
        setIsLeaderboardLoading(true);
        return;
      }

      const auditRows = Array.isArray((scLeaderboardData as any)?.getScDataLeaderboard)
        ? (scLeaderboardData as any).getScDataLeaderboard
        : [];

      if (auditRows.length === 0) {
        setLeaderboardEntries([]);
        setIsLeaderboardLoading(false);
        return;
      }

      try {
        setIsLeaderboardLoading(true);

        const auditIdList: string[] = auditRows
          .map((r: any) => String(r?.audit_id ?? "").trim())
          .filter(Boolean);

        // Pull completed actions from Strapi for these audits (may be empty -> 0 completed).
        // Matched on audit id only; with no ids there is nothing to count, and an
        // empty filter would return every ticket action.
        const ticketRes = auditIdList.length === 0
          ? null
          : await getTicketActions({
              context: { apiName: "cms" },
              variables: { filters: { auditId: { in: auditIdList } } },
            });

        const ticketItems = ticketRes?.data?.ticketActions?.data ?? [];

        // Deduplicate per auditId + actionKey using latest updatedAt, then count completed.
        const actionStateByAuditId = new Map<string, Map<string, { status: boolean; updatedAtMs: number }>>();
        ticketItems.forEach((row: any) => {
          const attrs = row?.attributes;
          const auditId = typeof attrs?.auditId === "string" ? attrs.auditId.trim() : String(attrs?.auditId ?? "").trim();
          if (!auditId) return;

          const category = typeof attrs?.category === "string" ? attrs.category.trim() : "";
          const section = typeof attrs?.section === "string" ? attrs.section.trim() : "";
          const notesId = attrs?.notesId === null || attrs?.notesId === undefined ? "" : String(attrs.notesId).trim();
          const actionSorting =
            attrs?.actionSorting === null || attrs?.actionSorting === undefined
              ? ""
              : String(attrs.actionSorting).trim();
          const question = typeof attrs?.question === "string" ? attrs.question.trim() : "";
          const response = typeof attrs?.response === "string" ? attrs.response.trim() : "";
          const actionName = typeof attrs?.actionName === "string" ? attrs.actionName.trim() : "";
          const legacyActionKey = getLegacyTicketActionKey(category, actionName);

          const actionKey =
            getTicketActionKey(category, section, question, response, notesId, actionSorting) ||
            legacyActionKey;
          if (!actionKey) return;
          const status = Boolean(attrs?.status);
          const updatedAt = typeof attrs?.updatedAt === "string" ? attrs.updatedAt : "";
          const updatedAtMs = updatedAt ? new Date(updatedAt).getTime() : 0;

          if (!actionStateByAuditId.has(auditId)) actionStateByAuditId.set(auditId, new Map());
          const map = actionStateByAuditId.get(auditId)!;
          const existing = map.get(actionKey);
          if (!existing || updatedAtMs >= existing.updatedAtMs) {
            map.set(actionKey, { status, updatedAtMs });
          }
        });

        const completedCountByAuditId = new Map<string, number>();
        actionStateByAuditId.forEach((actions, auditId) => {
          const completed = Array.from(actions.values()).reduce((sum, a) => sum + (a.status ? 1 : 0), 0);
          completedCountByAuditId.set(auditId, completed);
        });

        const computed: LeaderboardEntry[] = auditRows
          .map((row: any): LeaderboardEntry | null => {
            const auditId = String(row?.audit_id ?? "").trim();
            const occupier = String(row?.Occupier ?? row?.occupier ?? "").trim();
            const dateCompleted = formatDateCompleted(row?.DateCompleted);

            const rawPointPct = Number(row?.PointPercentage);
            const auditScorePercent = Number.isFinite(rawPointPct) ? Math.round(rawPointPct * 100) : 0;

            const rawActionCount = Number(row?.ActionCount);
            const total = Number.isFinite(rawActionCount) ? Math.max(0, Math.round(rawActionCount)) : 0;

            if (!auditId || !occupier) return null;

            const completed = completedCountByAuditId.get(auditId) ?? 0;
            const clampedCompleted = total > 0 ? Math.min(completed, total) : completed;
            const percentDone = total > 0 ? Math.round((clampedCompleted / total) * 100) : 0;

            return {
              auditId,
              occupier,
              dateCompleted,
              auditScorePercent,
              completed: clampedCompleted,
              total,
              percentDone,
            };
          })
          .filter(Boolean) as LeaderboardEntry[];

        computed.sort((a, b) => b.percentDone - a.percentDone || a.occupier.localeCompare(b.occupier));

        if (!isCurrent) return;
        setLeaderboardEntries(computed);
      } catch (err) {
        if (!isCurrent) return;
        console.log("Error loading leaderboard ticket actions:", err);
        setLeaderboardEntries([]);
      } finally {
        if (!isCurrent) return;
        setIsLeaderboardLoading(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [
    selectedCustomer,
    scLeaderboardData,
    scLeaderboardLoading,
    getCustomerName,
    getTicketActions,
    leaderboardRefreshKey,
    getTicketActionKey,
    getLegacyTicketActionKey,
  ]);

  const [getLegacyAudits, { data: legacyAuditsData }] = useLazyQuery(GET_AUDITS_DATA, {
    context: { apiName: "middleware" },
    fetchPolicy: "network-only",
    onError: (err) => {
      console.log("Error fetching legacy audits data:", err);
    },
  });

  // Initialize data on mount
  useEffect(() => {
    // Load filter data
    Storage.FILTER_DATA.get().then((filter) => {
      const start =
        filter.startDate.length > 0 ? new Date(filter.startDate) : null;
      const end = filter.endDate.length > 0 ? new Date(filter.endDate) : null;
      if (start && end) setDateRange([start, end]);
      else {
        setDateRange(previousMonthRangeSelect());
      }
    });

    // Load user profile data for username and email
    Storage.PROFILE_DATA.get().then((profile) => {
      if (profile) {
        setUsername(profile.name);
        setEmail(profile.email);
      }
    });

    // Load user data and customer list from storage
    Storage.USER_DATA.get()
      .then(async (customerList) => {
        if (customerList && customerList.length > 0) {
          setCustomerList(customerList);
          const filter = await Storage.FILTER_DATA.get();
          const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
          setSelectedCustomer(customerList[idx]);
          setIsLoading(false);
        } else {
          getCustomers();
        }
      })
      .catch(() => {
        getCustomers();
      });

    loadAllSustainabilityBanners();
  }, [getCustomers, loadAllSustainabilityBanners]);

  // Load achievements for selected customer and selected sites.
  // If no sites are explicitly selected, use all sites available for the selected customer.
  useEffect(() => {
    const customerNumber = extractBracketValueAsInt(selectedCustomer?.label);
    if (customerNumber === null) {
      setSustainabilityAchievements([]);
      return;
    }

    const sourceSites = (selectedSites ?? []).length > 0 ? selectedSites : sitesPerCustomer;
    const siteNumbers = sourceSites
      .map((site) => extractBracketValueAsInt(site?.label))
      .filter((val): val is number => val !== null);

    if (siteNumbers.length === 0) {
      setSustainabilityAchievements([]);
      return;
    }

    getSustainabilityAchievementsData({
      variables: {
        filters: {
          customerNumber: { eq: customerNumber },
          siteNumber: { in: siteNumbers },
          isActive: { eq: true },
        },
        sort: ["displayOrder:asc", "createdAt:desc"],
        pagination: { pageSize: 500 },
      },
    });
  }, [
    selectedCustomer,
    selectedSites,
    sitesPerCustomer,
    getSustainabilityAchievementsData,
    extractBracketValueAsInt,
  ]);

  // Keep selected sites in sync with the persisted filter selection.
  // FilterRow persists site selection by index in Storage.FILTER_DATA.
  useEffect(() => {
    let isCurrent = true;

    (async () => {
      if (!sitesPerCustomer || sitesPerCustomer.length === 0) {
        if (!isCurrent) return;
        setSelectedSites([]);
        return;
      }

      try {
        const filter = await Storage.FILTER_DATA.get();
        const siteList: SiteDataEntry[] = [];
        sitesPerCustomer.forEach((site, idx) => {
          if (filter.siteIndex?.includes(idx)) siteList.push(site);
        });
        if (!isCurrent) return;
        setSelectedSites(siteList);
      } catch {
        if (!isCurrent) return;
        setSelectedSites([]);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [sitesPerCustomer]);

  // Load Legacy audits table data (same source as Sustainability Audits page).
  // The V2 stored procedure is driven by sites, so send the selected ones and
  // fall back to every site under the customer when nothing is narrowed down.
  useEffect(() => {
    setLegacyTableHeader(genericDocumentsTable);

    if (!selectedCustomer || sitesCustomerId !== selectedCustomer.value) return;

    const sourceSites =
      (selectedSites ?? []).length > 0 ? selectedSites : sitesPerCustomer;
    const siteIdList = sourceSites
      .map((s) => parseInt(String(s.value), 10))
      .filter((n) => Number.isFinite(n));

    if (siteIdList.length === 0) return;

    // Audits are fetched for the selected customer only.
    const variables = { customerId: selectedCustomer.value, siteList: siteIdList };
    if (!isNewLegacyAuditsRequest(variables)) return;
    getLegacyAudits({ variables });
  }, [
    getLegacyAudits,
    selectedCustomer,
    sitesCustomerId,
    selectedSites,
    sitesPerCustomer,
    isNewLegacyAuditsRequest,
  ]);

  useEffect(() => {
    if (!legacyAuditsData?.getCustomers) return;

    const byId: Record<string, any[]> = {};
    legacyAuditsData.getCustomers.forEach((elem: any) => {
      if (elem?.id) byId[String(elem.id)] = elem.sustainabilityAudit ?? [];
    });
    setLegacyAuditsByCustomerId(byId);
  }, [legacyAuditsData]);

  useEffect(() => {
    if (!selectedCustomer) {
      setLegacyTableRows([]);
      return;
    }

    const audits = legacyAuditsByCustomerId[String(selectedCustomer.value)] ?? [];
    const parsed = auditsDataFormat(audits);
    setLegacyTableRows(parsed);
  }, [selectedCustomer, legacyAuditsByCustomerId]);

  const handleLegacySortChanged = useCallback(
    (col: number) => {
      setLegacySortSettings((current) => ({
        column: col,
        order:
          current?.column === col && current?.order === SortOrder.Ascending
            ? SortOrder.Descending
            : SortOrder.Ascending,
      }));
      setLegacyReordering(true);
    },
    []
  );

  const handleLegacyFilterChanged = useCallback((val: string) => {
    setLegacySearchParam(val);
  }, []);

  const resetLegacyTable = useCallback(() => {
    setLegacySearchParam("");
    setLegacySortSettings(undefined);
  }, []);

  useEffect(() => {
    if (legacySortSettings && legacyReordering) {
      setLegacyReordering(false);
      setLegacyTableRows((rows) => [...rows].sort((a, b) => sortTableFunction(a, b, legacySortSettings)));
    }
  }, [legacySortSettings, legacyReordering]);

  useLayoutEffect(() => {
    setWidth(window.innerWidth);
  }, []);

  const handleWindowResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [handleWindowResize]);

  // Process customers data (only when fetched from GraphQL)
  useEffect(() => {
    if (customersData?.getCustomers) {
      // Sites are loaded later, for the selected customer only.
      const customers = customerListFormat(customersData.getCustomers);
      setCustomerList(customers);
      if (customers.length > 0) {
        setSelectedCustomer(customers[0]);
      }
    }
  }, [customersData]);

  useEffect(() => {
    if (!selectedCustomer) {
      setAuditOptions([]);
      setSelectedAudit(null);
      return;
    }

    // Wait for the selected customer's sites. Otherwise the call goes out with
    // the previous customer's sites and auto-selects one of its audits.
    if (sitesCustomerId !== selectedCustomer.value) {
      setAuditOptions([]);
      setSelectedAudit(null);
      return;
    }

    const sourceSites = (selectedSites ?? []).length > 0 ? selectedSites : sitesPerCustomer;
    const siteIdList = sourceSites
      .map((s) => parseInt(String(s.value), 10))
      .filter((n) => Number.isFinite(n));

    // Skip the call when the effect re-runs with the same sites.
    if (!isNewScRequest(siteIdList)) return;

    setAuditOptions([]);
    setSelectedAudit(null);

    getScDataList({
      variables: {
        siteId: siteIdList,
      },
    });

    getScDataLeaderboard({
      variables: {
        siteId: siteIdList,
      },
    });
  }, [
    selectedCustomer,
    sitesCustomerId,
    selectedSites,
    sitesPerCustomer,
    getScDataList,
    getScDataLeaderboard,
    isNewScRequest,
  ]);

  const formatDateCompleted = (value: unknown): string => {
    if (value === null || value === undefined) return "";

    let date: Date | null = null;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "number" && Number.isFinite(value)) {
      const ms = value < 1e12 ? value * 1000 : value;
      date = new Date(ms);
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      const asNumber = Number(trimmed);
      if (trimmed !== "" && Number.isFinite(asNumber)) {
        const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
        date = new Date(ms);
      } else {
        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
          date = parsed;
        }
      }
    }

    if (!date || Number.isNaN(date.getTime())) {
      return String(value);
    }

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (!scDataListData?.getScDataList) {
      setAuditOptions([]);
      return;
    }

    const getOccupierFromScListItem = (item: any): string => {
      const raw = item?.Occupier ?? item?.occupier ?? item?.auditOccupier;
      if (typeof raw === "string") return raw.trim();
      if (raw === null || raw === undefined) return "";
      return String(raw).trim();
    };

    const formatAuditLabel = (site: unknown, occupier: unknown, dateCompleted: unknown): string => {
      const siteStr = String(site ?? "").trim();
      const occStr = String(occupier ?? "").trim() || "(unknown occupier)";
      const dateStr = formatDateCompleted(dateCompleted);

      if (siteStr && dateStr) return `${occStr} - ${siteStr} - ${dateStr}`;
      if (siteStr) return `${occStr} - ${siteStr}`;
      return `${occStr} - ${dateStr}`.trim();
    };

    const options = scDataListData.getScDataList.map((item: any) => ({
      value: String(item.audit_id),
      label: formatAuditLabel(item.CustomerSite, getOccupierFromScListItem(item), item.DateCompleted),
      customerSite: item.CustomerSite,
      occupier: getOccupierFromScListItem(item),
      dateCompleted: item.DateCompleted,
      handlebarUri: typeof item?.handlebar_uri === "string" ? item.handlebar_uri : "",
      carboneTemplateId: typeof item?.carbone_template_id === "string" ? item.carbone_template_id : "",
    }));

    setAuditOptions(options);
  }, [scDataListData]);

  // When audits load for a customer, automatically select the first audit.
  useEffect(() => {
    if (selectedAudit) return;
    if (!auditOptions || auditOptions.length === 0) return;
    setSelectedAudit(auditOptions[0]);
  }, [auditOptions, selectedAudit]);

  const sanitizeFilenamePart = (value: unknown): string => {
    const str = value === null || value === undefined ? "" : String(value);
    return str
      .trim()
      .replaceAll("/", "-")
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .slice(0, 120);
  };

  // PDF Export Handler using Carbone
  const handleExportPDF = useCallback(async () => {
    try {
      if (!selectedCustomer || !selectedAudit) {
        alert("Please select a customer and audit first");
        return;
      }

      setExportInProgress(true);
      setDownloadProgress(null);

      let payload =
        cachedSafetyCultureAuditId === String(selectedAudit.value)
          ? cachedSafetyCulturePayload
          : undefined;

      if (!payload) {
        const handlebarResult = await getSafetyCultureHandlebar({
          variables: {
            auditId: selectedAudit.value,
            templateUri:
              (typeof selectedAudit?.handlebarUri === "string" ? selectedAudit.handlebarUri.trim() : "") ||
              process.env.REACT_APP_SC_HANDLEBAR_TEMPLATE_URL,
            customerId: parseInt(String(selectedCustomer.value), 10),
          },
        });

        payload = handlebarResult.data?.getSafetyCultureHandlebar;
      }

      if (!payload) {
        alert("No data available for export. Please try again.");
        return;
      }

      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (error) {
          console.warn("SafetyCulture payload is not valid JSON:", error);
        }
      }

      setCachedSafetyCultureAuditId(String(selectedAudit.value));
      setCachedSafetyCulturePayload(payload);

      const templateId =
        (typeof selectedAudit?.carboneTemplateId === "string" ? selectedAudit.carboneTemplateId.trim() : "") ||
        process.env.REACT_APP_CARBONE_REPORT_SC_TEMPLATE_PDF;
      
      // Build filename using CustomerSite and DateCompleted from getScDataList
      const safeCustomerSite = sanitizeFilenamePart(selectedAudit.customerSite ?? "Audit");
      const rawDateCompleted = formatDateCompleted(selectedAudit.dateCompleted) || "unknown";
      const safeDateCompleted = sanitizeFilenamePart(rawDateCompleted);
      const filename = `SustainabilityAudit_${safeCustomerSite}_${safeDateCompleted}.pdf`;

      // Enhanced download configuration with progress tracking
      const downloadConfig = createDownloadConfig(
        (progress) => {
          setDownloadProgress(progress);
          console.log(`Download progress: ${progress.percentage}%`);
        },
        300000, // 5 minute timeout
        'arraybuffer'
      );

      console.log('[SafetyCulture Export] Starting PDF download...');
      const response = await axios.post(
        `${process.env.REACT_APP_CARBONE_RENDER_ENDPOINT}/render/${templateId}?download=true`,
        {
          data: payload,
          convertTo: 'pdf',
        },
        {
          headers: {
            "Content-Type": "application/json",
            "carbone-version": "5",
            Authorization: `Bearer ${process.env.REACT_APP_CARBONE_API_KEY}`,
          },
          ...downloadConfig
        }
      );

      if (response.status === 200) {
        console.log('[SafetyCulture Export] Download completed, validating file...');
        
        const contentLength = response.headers['content-length'] 
          ? parseInt(response.headers['content-length'], 10) 
          : undefined;

        const validation = await validateDownloadCompleteness(
          response.data,
          contentLength,
          'application/pdf'
        );

        console.log('[SafetyCulture Export] Validation result:', validation);

        if (!validation.isValid) {
          console.error('[SafetyCulture Export] File validation failed:', validation.errors);
          alert(`Download failed: ${validation.errors.join(', ')}`);
          return;
        }

        if (validation.warnings.length > 0) {
          console.warn('[SafetyCulture Export] Validation warnings:', validation.warnings);
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        console.log(`[SafetyCulture Export] File validated successfully. Size: ${blob.size} bytes`);

        // Download the file
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        // Stops Matomo's automatic link tracking recording the blob: URL; tracked explicitly below
        link.className = "matomo_ignore";
        document.body.appendChild(link);
        link.click();
        trackDownload(filename, "carbone-audit-report");

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        }, 100);
        
        console.log('[SafetyCulture Export] Download initiated successfully');
        
        if (validation.warnings.length > 0) {
          alert(`File downloaded successfully with warnings:\n${validation.warnings.join('\n')}`);
        } else {
          console.log(`File downloaded successfully: ${filename} (${blob.size} bytes)`);
        }
      } else {
        console.error(`[SafetyCulture Export] Unexpected response status: ${response.status}`);
        alert(`Export failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error("[SafetyCulture Export Error]", err);
      
      let errorMessage = "Export failed. Try again later.";
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = "Download timeout. The file may be too large or the connection is slow.";
      } else if (err.response?.status === 413) {
        errorMessage = "File too large to download.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error during file generation.";
      } else if (err.response?.status) {
        errorMessage = `Server error: ${err.response.status} - ${err.response.statusText}`;
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setExportInProgress(false);
      setDownloadProgress(null);
    }
  }, [selectedCustomer, selectedAudit, getSafetyCultureHandlebar, cachedSafetyCultureAuditId, cachedSafetyCulturePayload]);

  // Get report button handler (fetch data for display)
  const handleGetReport = useCallback(() => {
    if (!selectedCustomer) {
      alert("Please select customer");
      return;
    }

    setIsLoading(true);
    
    const siteId: any[] = [];
    
    // Use current month date range by default
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    getHomeData({
      variables: {
        data: {
          customerId: selectedCustomer.value,
          siteId: siteId,
          wasteTypes: [], // No waste types filter
          startDate: inputDateFormat(firstDay),
          endDate: inputDateFormat(lastDay),
        },
      },
    });
  }, [selectedCustomer, getHomeData]);

  // Process report data
  useEffect(() => {
    if (homeData?.getHomeData) {
      setReportData(homeData.getHomeData);
    }
  }, [homeData]);

  const sustainabilityDashboardProps = useMemo(
    () => ({
      isLoading,
      customerList,
      selectedCustomer,
      sitesPerCustomer,
      reportData,
      auditOptions,
      selectedAudit,
      isAuditListLoading: scDataListLoading,
      username,
      email,
      sustainabilityBanners,
      sustainabilityAchievements,
      isSustainabilityAchievementsLoading: sustainabilityAchievementsLoading,
      progressiveActionGroups,
      ticketActionStatusByName,
      ticketActionCompletedByByName,
      isTicketActionsLoading,
      handleToggleTicketAction,
      leaderboardEntries,
      isLeaderboardLoading,
      auditActionsTableHeader,
      auditActionsTableRows,
      auditActionsSortSettings,
      auditActionsSearchParam,
      resetAuditActionsTable,
      isAuditNotesLoading,
      legacyTableHeader,
      legacyTableRows,
      legacySortSettings,
      legacySearchParam,
      handleLegacySortChanged,
      handleLegacyFilterChanged,
      resetLegacyTable,
      handleAuditActionsSortChanged: (col: number) => {
        setAuditActionsSortSettings((current) => ({
          column: col,
          order:
            current?.column === col && current?.order === SortOrder.Ascending
              ? SortOrder.Descending
              : SortOrder.Ascending,
        }));
      },
      handleAuditActionsFilterChanged: (val: string) => {
        setAuditActionsSearchParam(val);
      },
      exportInProgress,
      downloadProgress,
      setSelectedCustomer,
      setSelectedSites,
      setSelectedAudit,
      handleGetReport,
      handleExportPDF,
      darkMode,
      width,
    }),
    [
      isLoading,
      customerList,
      selectedCustomer,
      sitesPerCustomer,
      reportData,
      auditOptions,
      selectedAudit,
      scDataListLoading,
      username,
      email,
      sustainabilityBanners,
      sustainabilityAchievements,
      sustainabilityAchievementsLoading,
      progressiveActionGroups,
      ticketActionStatusByName,
      ticketActionCompletedByByName,
      isTicketActionsLoading,
      handleToggleTicketAction,
      leaderboardEntries,
      isLeaderboardLoading,
      auditActionsTableHeader,
      auditActionsTableRows,
      auditActionsSortSettings,
      auditActionsSearchParam,
      resetAuditActionsTable,
      isAuditNotesLoading,
      legacyTableHeader,
      legacyTableRows,
      legacySortSettings,
      legacySearchParam,
      handleLegacySortChanged,
      handleLegacyFilterChanged,
      resetLegacyTable,
      exportInProgress,
      downloadProgress,
      handleGetReport,
      handleExportPDF,
      setSelectedAudit,
      darkMode,
      width,
    ]
  );

  return <SustainabilityDashboardUI {...sustainabilityDashboardProps} />;
};

export default SustainabilityDashboard;
